import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import {
  Brain, TrendingUp, Target, Users, LineChart as LineChartIcon,
  DollarSign, Calendar as CalendarIcon, Search, Filter, Trash2,
  Plus, FolderOpen, Sparkles, Loader2, ArrowRight, X, Globe, Tag,
  FileDown
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import CpvBadge from '@/components/CpvBadge';

const COLORS = ['hsl(var(--primary))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))', '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F'];

interface Dataset {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  item_count?: number;
}

interface TedResult {
  id: string;
  ted_notice_id: string;
  title: string;
  description: string | null;
  buyer_name: string | null;
  country: string | null;
  cpv_codes: string[] | null;
  estimated_value: number | null;
  currency: string | null;
  publication_date: string | null;
  deadline_date: string | null;
  ted_url: string | null;
}

export default function DataAnalysis() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Datasets
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState<string>('');
  const [datasetItems, setDatasetItems] = useState<TedResult[]>([]);
  const [loadingDatasets, setLoadingDatasets] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCountry, setFilterCountry] = useState<string[]>([]);
  const [filterCpv, setFilterCpv] = useState<string[]>([]);
  const [filterBuyer, setFilterBuyer] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // AI Analysis
  const [analysisType, setAnalysisType] = useState<string>('full');
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [analysisStats, setAnalysisStats] = useState<any>(null);
  const [analyzing, setAnalyzing] = useState(false);

  // New dataset dialog
  const [newDatasetName, setNewDatasetName] = useState('');

  useEffect(() => {
    fetchDatasets();
  }, []);

  useEffect(() => {
    if (selectedDatasetId) fetchDatasetItems(selectedDatasetId);
  }, [selectedDatasetId]);

  const fetchDatasets = async () => {
    try {
      const { data: ds, error } = await supabase
        .from('analysis_datasets')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;

      // Count items per dataset
      const withCounts = await Promise.all((ds || []).map(async (d: any) => {
        const { count } = await supabase
          .from('analysis_dataset_items')
          .select('id', { count: 'exact', head: true })
          .eq('dataset_id', d.id);
        return { ...d, item_count: count || 0 };
      }));

      setDatasets(withCounts);
      if (withCounts.length > 0 && !selectedDatasetId) {
        setSelectedDatasetId(withCounts[0].id);
      }
    } catch (error) {
      console.error('Error fetching datasets:', error);
    } finally {
      setLoadingDatasets(false);
    }
  };

  const fetchDatasetItems = async (datasetId: string) => {
    setLoadingItems(true);
    try {
      const { data: items, error: itemsError } = await supabase
        .from('analysis_dataset_items')
        .select('ted_result_id')
        .eq('dataset_id', datasetId);
      if (itemsError) throw itemsError;
      if (!items || items.length === 0) {
        setDatasetItems([]);
        return;
      }

      const ids = items.map(i => i.ted_result_id);
      const { data: results, error: resError } = await supabase
        .from('ted_results')
        .select('*')
        .in('id', ids)
        .order('publication_date', { ascending: false });
      if (resError) throw resError;
      setDatasetItems(results || []);
    } catch (error) {
      console.error('Error fetching dataset items:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  const createDataset = async () => {
    if (!newDatasetName.trim() || !user) return;
    try {
      const { data, error } = await supabase
        .from('analysis_datasets')
        .insert({ name: newDatasetName.trim(), user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      setDatasets(prev => [{ ...data, item_count: 0 }, ...prev]);
      setSelectedDatasetId(data.id);
      setNewDatasetName('');
      toast({ title: t('dataAnalysis.datasetCreated', 'Dataset created') });
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    }
  };

  const deleteDataset = async (id: string) => {
    try {
      const { error } = await supabase.from('analysis_datasets').delete().eq('id', id);
      if (error) throw error;
      setDatasets(prev => prev.filter(d => d.id !== id));
      if (selectedDatasetId === id) {
        setSelectedDatasetId(datasets.find(d => d.id !== id)?.id || '');
      }
      toast({ title: t('dataAnalysis.datasetDeleted', 'Dataset deleted') });
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    }
  };

  const removeItem = async (tedResultId: string) => {
    try {
      const { error } = await supabase
        .from('analysis_dataset_items')
        .delete()
        .eq('dataset_id', selectedDatasetId)
        .eq('ted_result_id', tedResultId);
      if (error) throw error;
      setDatasetItems(prev => prev.filter(i => i.id !== tedResultId));
      setDatasets(prev => prev.map(d => d.id === selectedDatasetId ? { ...d, item_count: (d.item_count || 1) - 1 } : d));
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    }
  };

  const runAnalysis = async () => {
    if (!selectedDatasetId || datasetItems.length === 0) return;
    setAnalyzing(true);
    setAnalysisResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-dataset', {
        body: { datasetId: selectedDatasetId, analysisType, language: i18n.language }
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setAnalysisResult(data.analysis);
      setAnalysisStats(data.stats);
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    } finally {
      setAnalyzing(false);
    }
  };

  const exportPdf = async () => {
    if (!analysisResult) return;
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    let y = 20;

    const addPageIfNeeded = (needed: number) => {
      if (y + needed > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        y = 20;
      }
    };

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(selectedDataset?.name || 'Data Analysis', margin, y);
    y += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`${t('dataAnalysis.generatedOn', 'Generated on')} ${new Date().toLocaleDateString()} • ${filteredItems.length} ${t('dataAnalysis.tenders', 'tenders')}`, margin, y);
    doc.setTextColor(0);
    y += 10;

    // Stats
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(t('dataAnalysis.overview', 'Overview'), margin, y);
    y += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const stats = [
      `${t('dataAnalysis.totalTenders', 'Total Tenders')}: ${filteredItems.length}`,
      `${t('dataAnalysis.avgValue', 'Average Value')}: €${chartData.avgValue.toLocaleString()}`,
      `${t('dataAnalysis.totalValueLabel', 'Total Value')}: €${chartData.totalValue.toLocaleString()}`,
      `${t('dataAnalysis.countries', 'Countries')}: ${chartData.countries.length}`,
    ];
    stats.forEach(s => { doc.text(s, margin, y); y += 5; });
    y += 4;

    // --- Bar chart helper ---
    const drawBarChart = (title: string, data: { name: string; value: number }[], barColor: [number, number, number]) => {
      const chartH = 8 + data.length * 12;
      addPageIfNeeded(chartH + 14);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text(title, margin, y);
      y += 7;

      const maxVal = Math.max(...data.map(d => d.value), 1);
      const barMaxW = pageW - margin * 2 - 50;

      data.forEach(d => {
        const barW = (d.value / maxVal) * barMaxW;
        // Label
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60);
        doc.text(d.name, margin, y + 3);
        // Bar
        doc.setFillColor(...barColor);
        doc.roundedRect(margin + 42, y - 3, barW, 8, 1, 1, 'F');
        // Value
        doc.setTextColor(0);
        doc.setFontSize(7);
        doc.text(String(d.value), margin + 42 + barW + 2, y + 3);
        y += 12;
      });
      y += 4;
    };

    // Countries chart
    if (chartData.countries.length > 0) {
      drawBarChart(
        t('dataAnalysis.byCountry', 'By Country'),
        chartData.countries.slice(0, 10),
        [59, 130, 246]
      );
    }

    // CPV chart
    if (chartData.cpv.length > 0) {
      drawBarChart(
        t('dataAnalysis.byCpv', 'By CPV Division'),
        chartData.cpv.slice(0, 10),
        [16, 185, 129]
      );
    }
    if (analysisResult.summary) {
      addPageIfNeeded(30);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(t('dataAnalysis.summary', 'Summary'), margin, y);
      y += 6;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(analysisResult.summary, pageW - margin * 2);
      lines.forEach((line: string) => { addPageIfNeeded(5); doc.text(line, margin, y); y += 4.5; });
      y += 4;
    }

    // Trends
    if (analysisResult.trends?.length) {
      addPageIfNeeded(20);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(t('dataAnalysis.trendsTitle', 'Trends'), margin, y);
      y += 6;
      analysisResult.trends.forEach((tr: any) => {
        addPageIfNeeded(15);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`[${tr.impact || ''}] ${tr.title}`, margin, y);
        y += 4.5;
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(tr.description || '', pageW - margin * 2);
        lines.forEach((l: string) => { addPageIfNeeded(5); doc.text(l, margin, y); y += 4.5; });
        y += 2;
      });
      y += 2;
    }

    // Top Opportunities
    if (analysisResult.topOpportunities?.length) {
      addPageIfNeeded(20);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(t('dataAnalysis.topOpportunities', 'Top Opportunities'), margin, y);
      y += 6;
      analysisResult.topOpportunities.forEach((op: any, i: number) => {
        addPageIfNeeded(15);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`${op.score || i + 1}. ${op.title}${op.country ? ` (${op.country})` : ''}`, margin, y);
        y += 4.5;
        doc.setFont('helvetica', 'normal');
        if (op.reason) {
          const lines = doc.splitTextToSize(op.reason, pageW - margin * 2);
          lines.forEach((l: string) => { addPageIfNeeded(5); doc.text(l, margin, y); y += 4.5; });
        }
        y += 2;
      });
      y += 2;
    }

    // Competitive Analysis
    if (analysisResult.competitiveAnalysis) {
      addPageIfNeeded(20);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(t('dataAnalysis.competitiveTitle', 'Competitive Landscape'), margin, y);
      y += 6;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      if (analysisResult.competitiveAnalysis.marketConcentration) {
        const lines = doc.splitTextToSize(analysisResult.competitiveAnalysis.marketConcentration, pageW - margin * 2);
        lines.forEach((l: string) => { addPageIfNeeded(5); doc.text(l, margin, y); y += 4.5; });
        y += 2;
      }
      analysisResult.competitiveAnalysis.insights?.forEach((ins: string) => {
        addPageIfNeeded(10);
        const lines = doc.splitTextToSize(`• ${ins}`, pageW - margin * 2);
        lines.forEach((l: string) => { addPageIfNeeded(5); doc.text(l, margin, y); y += 4.5; });
      });
      y += 4;
    }

    // Dominant Buyers
    if (analysisResult.dominantBuyers?.length) {
      addPageIfNeeded(20);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(t('dataAnalysis.dominantBuyers', 'Dominant Buyers'), margin, y);
      y += 6;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      analysisResult.dominantBuyers.forEach((b: any) => {
        addPageIfNeeded(8);
        doc.text(`${b.name} — ${b.tenders} ${t('dataAnalysis.tenders', 'tenders')}${b.totalValue ? ` • ${b.totalValue}` : ''}`, margin, y);
        y += 5;
      });
      y += 4;
    }

    // Predictions
    if (analysisResult.predictions?.length) {
      addPageIfNeeded(20);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(t('dataAnalysis.predictionsTitle', 'Predictions'), margin, y);
      y += 6;
      analysisResult.predictions.forEach((p: any) => {
        addPageIfNeeded(15);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`[${p.trend}] ${p.sector}${p.country ? ` (${p.country})` : ''}${p.confidence ? ` — ${p.confidence}` : ''}`, margin, y);
        y += 4.5;
        doc.setFont('helvetica', 'normal');
        if (p.reasoning) {
          const lines = doc.splitTextToSize(p.reasoning, pageW - margin * 2);
          lines.forEach((l: string) => { addPageIfNeeded(5); doc.text(l, margin, y); y += 4.5; });
        }
        y += 2;
      });
      y += 2;
    }

    // Pricing
    if (analysisResult.pricing?.trends?.length) {
      addPageIfNeeded(20);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(t('dataAnalysis.pricingTitle', 'Pricing Analysis'), margin, y);
      y += 6;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      analysisResult.pricing.trends.forEach((tr: string) => {
        addPageIfNeeded(10);
        const lines = doc.splitTextToSize(`• ${tr}`, pageW - margin * 2);
        lines.forEach((l: string) => { addPageIfNeeded(5); doc.text(l, margin, y); y += 4.5; });
      });
      y += 4;
    }

    // Price Ranges table
    if (analysisResult.priceRanges?.length) {
      addPageIfNeeded(30);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(t('dataAnalysis.priceRanges', 'Price Ranges by Sector'), margin, y);
      y += 6;
      autoTable(doc, {
        startY: y,
        head: [['Sector', 'Min (€)', 'Max (€)', 'Avg (€)']],
        body: analysisResult.priceRanges.map((pr: any) => [
          pr.sector, pr.min?.toLocaleString(), pr.max?.toLocaleString(), pr.average?.toLocaleString()
        ]),
        margin: { left: margin },
        styles: { fontSize: 8 },
      });
      y = (doc as any).lastAutoTable.finalY + 8;
    }

    // Recommendations
    if (analysisResult.recommendations?.length) {
      addPageIfNeeded(20);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(t('dataAnalysis.recommendations', 'Recommendations'), margin, y);
      y += 6;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      analysisResult.recommendations.forEach((rec: string, i: number) => {
        addPageIfNeeded(10);
        const lines = doc.splitTextToSize(`${i + 1}. ${rec}`, pageW - margin * 2);
        lines.forEach((l: string) => { addPageIfNeeded(5); doc.text(l, margin, y); y += 4.5; });
        y += 1;
      });
      y += 4;
    }

    // Tenders list table
    addPageIfNeeded(30);
    doc.addPage();
    y = 20;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(t('dataAnalysis.tendersListTitle', 'Tenders List'), margin, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [[t('dataAnalysis.tenderTitle', 'Title'), t('dataAnalysis.buyer', 'Buyer'), t('dataAnalysis.country', 'Country'), t('dataAnalysis.valuePdf', 'Value (€)'), t('dataAnalysis.date', 'Date')]],
      body: filteredItems.slice(0, 200).map(item => [
        item.title.substring(0, 60) + (item.title.length > 60 ? '...' : ''),
        item.buyer_name || '-',
        item.country || '-',
        item.estimated_value ? `€${item.estimated_value.toLocaleString()}` : '-',
        item.publication_date || '-'
      ]),
      margin: { left: margin },
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [59, 130, 246] },
    });

    const fileName = `analysis_${(selectedDataset?.name || 'report').replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);
    toast({ title: t('dataAnalysis.pdfExported', 'PDF report exported') });
  };

  // Filtered items
  const filteredItems = useMemo(() => {
    return datasetItems.filter(r => {
      if (searchQuery && !r.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !r.buyer_name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterCountry.length > 0 && (!r.country || !filterCountry.includes(r.country))) return false;
      if (filterCpv.length > 0 && (!r.cpv_codes || !r.cpv_codes.some(c => filterCpv.includes(c.substring(0, 2))))) return false;
      if (filterBuyer.length > 0 && (!r.buyer_name || !filterBuyer.includes(r.buyer_name))) return false;
      if (dateFrom && r.publication_date && new Date(r.publication_date) < dateFrom) return false;
      if (dateTo && r.publication_date && new Date(r.publication_date) > dateTo) return false;
      if (minValue && r.estimated_value && r.estimated_value < parseFloat(minValue)) return false;
      if (maxValue && r.estimated_value && r.estimated_value > parseFloat(maxValue)) return false;
      return true;
    });
  }, [datasetItems, searchQuery, filterCountry, filterCpv, filterBuyer, dateFrom, dateTo, minValue, maxValue]);

  // Stats for charts
  const chartData = useMemo(() => {
    const countries: Record<string, number> = {};
    const cpvDivisions: Record<string, number> = {};
    const monthly: Record<string, number> = {};
    const values: number[] = [];

    filteredItems.forEach(r => {
      if (r.country) countries[r.country] = (countries[r.country] || 0) + 1;
      if (r.cpv_codes) r.cpv_codes.forEach(c => {
        const div = c.substring(0, 2);
        cpvDivisions[div] = (cpvDivisions[div] || 0) + 1;
      });
      if (r.publication_date) {
        const m = r.publication_date.substring(0, 7);
        monthly[m] = (monthly[m] || 0) + 1;
      }
      if (r.estimated_value) values.push(r.estimated_value);
    });

    return {
      countries: Object.entries(countries).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([name, value]) => ({ name, value })),
      cpv: Object.entries(cpvDivisions).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([name, value]) => ({ name: `CPV ${name}`, value })),
      monthly: Object.entries(monthly).sort((a, b) => a[0].localeCompare(b[0])).map(([name, value]) => ({ name, value })),
      avgValue: values.length > 0 ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : 0,
      totalValue: values.reduce((a, b) => a + b, 0),
      minVal: values.length > 0 ? Math.min(...values) : 0,
      maxVal: values.length > 0 ? Math.max(...values) : 0,
    };
  }, [filteredItems]);

  const availableCountries = [...new Set(datasetItems.map(r => r.country).filter(Boolean))] as string[];
  const availableBuyers = [...new Set(datasetItems.map(r => r.buyer_name).filter(Boolean))] as string[];

  const analysisModules = [
    { key: 'full', icon: Brain, label: t('dataAnalysis.fullAnalysis', 'Full Analysis'), color: 'text-purple-500' },
    { key: 'trends', icon: TrendingUp, label: t('dataAnalysis.trends', 'Trends & Patterns'), color: 'text-blue-500' },
    { key: 'scoring', icon: Target, label: t('dataAnalysis.scoring', 'Opportunity Scoring'), color: 'text-green-500' },
    { key: 'competitive', icon: Users, label: t('dataAnalysis.competitive', 'Competitive Analysis'), color: 'text-orange-500' },
    { key: 'predictions', icon: LineChartIcon, label: t('dataAnalysis.predictions', 'Predictions'), color: 'text-cyan-500' },
    { key: 'pricing', icon: DollarSign, label: t('dataAnalysis.pricing', 'Price Analysis'), color: 'text-emerald-500' },
  ];

  const selectedDataset = datasets.find(d => d.id === selectedDatasetId);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Brain className="w-8 h-8 text-primary" />
              {t('dataAnalysis.title', 'Data Analysis')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('dataAnalysis.subtitle', 'Advanced analysis with AI on your selected tenders')}
            </p>
          </div>
        </div>

        {/* Dataset selector */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              {t('dataAnalysis.datasets', 'Datasets')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 flex-wrap">
              <Select value={selectedDatasetId} onValueChange={setSelectedDatasetId}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder={t('dataAnalysis.selectDataset', 'Select a dataset')} />
                </SelectTrigger>
                <SelectContent>
                  {datasets.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} ({d.item_count} {t('dataAnalysis.tenders', 'tenders')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <Input
                  value={newDatasetName}
                  onChange={e => setNewDatasetName(e.target.value)}
                  placeholder={t('dataAnalysis.newDatasetPlaceholder', 'New dataset name...')}
                  className="w-48"
                  onKeyDown={e => e.key === 'Enter' && createDataset()}
                />
                <Button size="sm" onClick={createDataset} disabled={!newDatasetName.trim()}>
                  <Plus className="w-4 h-4 mr-1" />
                  {t('common.create')}
                </Button>
              </div>

              {selectedDatasetId && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="text-destructive">
                      <Trash2 className="w-4 h-4 mr-1" />
                      {t('common.delete')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('dataAnalysis.deleteDatasetTitle', 'Delete dataset?')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('dataAnalysis.deleteDatasetDesc', 'This will permanently delete the dataset and all its items.')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteDataset(selectedDatasetId)}>{t('common.delete')}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>

            {datasets.length === 0 && !loadingDatasets && (
              <Alert className="mt-4">
                <AlertDescription>
                  {t('dataAnalysis.noDatasetsHint', 'Create a dataset, then go to Results to select tenders to analyze.')}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {selectedDatasetId && (
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList>
              <TabsTrigger value="overview">{t('dataAnalysis.overview', 'Overview')}</TabsTrigger>
              <TabsTrigger value="data">{t('dataAnalysis.data', 'Data')} ({filteredItems.length})</TabsTrigger>
              <TabsTrigger value="ai">{t('dataAnalysis.aiAnalysis', 'AI Analysis')}</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4">
              {loadingItems ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
                </div>
              ) : datasetItems.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">{t('dataAnalysis.emptyDataset', 'Empty dataset')}</p>
                    <p className="mt-1">{t('dataAnalysis.goToResults', 'Go to Results to select tenders for analysis')}</p>
                    <Button className="mt-4" onClick={() => navigate('/results')}>
                      <ArrowRight className="w-4 h-4 mr-2" />
                      {t('dataAnalysis.goToResultsBtn', 'Go to Results')}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Stats cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold">{filteredItems.length}</div>
                        <p className="text-sm text-muted-foreground">{t('dataAnalysis.totalTenders', 'Total Tenders')}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold">€{chartData.avgValue.toLocaleString()}</div>
                        <p className="text-sm text-muted-foreground">{t('dataAnalysis.avgValue', 'Average Value')}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold">{chartData.countries.length}</div>
                        <p className="text-sm text-muted-foreground">{t('dataAnalysis.countries', 'Countries')}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold">€{chartData.totalValue.toLocaleString()}</div>
                        <p className="text-sm text-muted-foreground">{t('dataAnalysis.totalValueLabel', 'Total Value')}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.byCountry', 'By Country')}</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={chartData.countries}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" fontSize={12} />
                            <YAxis />
                            <RechartsTooltip />
                            <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.byCpv', 'By CPV Division')}</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie data={chartData.cpv} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                              {chartData.cpv.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <RechartsTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {chartData.monthly.length > 1 && (
                      <Card className="lg:col-span-2">
                        <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.monthlyTrend', 'Monthly Trend')}</CardTitle></CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={chartData.monthly}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" fontSize={12} />
                              <YAxis />
                              <RechartsTooltip />
                              <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </>
              )}
            </TabsContent>

            {/* Data Tab */}
            <TabsContent value="data" className="space-y-4">
              {/* Filters */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder={t('dataAnalysis.searchPlaceholder', 'Search by title or buyer...')}
                        className="pl-9"
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
                      <Filter className="w-4 h-4 mr-1" />
                      {t('common.filter')}
                    </Button>
                  </div>

                  {showFilters && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                      <div>
                        <Label className="text-xs">{t('dataAnalysis.country', 'Country')}</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {availableCountries.slice(0, 10).map(c => (
                            <Badge
                              key={c}
                              variant={filterCountry.includes(c) ? 'default' : 'outline'}
                              className="cursor-pointer text-xs"
                              onClick={() => setFilterCountry(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c])}
                            >
                              {c}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">{t('dataAnalysis.valueRange', 'Value Range (€)')}</Label>
                        <div className="flex gap-2 mt-1">
                          <Input type="number" value={minValue} onChange={e => setMinValue(e.target.value)} placeholder="Min" className="text-xs" />
                          <Input type="number" value={maxValue} onChange={e => setMaxValue(e.target.value)} placeholder="Max" className="text-xs" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">{t('dataAnalysis.dates', 'Date Range')}</Label>
                        <div className="flex gap-2 mt-1">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" className="text-xs w-full justify-start">
                                <CalendarIcon className="w-3 h-3 mr-1" />
                                {dateFrom ? dateFrom.toLocaleDateString() : t('dataAnalysis.from', 'From')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} /></PopoverContent>
                          </Popover>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" className="text-xs w-full justify-start">
                                <CalendarIcon className="w-3 h-3 mr-1" />
                                {dateTo ? dateTo.toLocaleDateString() : t('dataAnalysis.to', 'To')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={dateTo} onSelect={setDateTo} /></PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Items list */}
              <div className="space-y-2">
                {loadingItems ? (
                  [...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)
                ) : filteredItems.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      {datasetItems.length === 0
                        ? t('dataAnalysis.emptyDataset', 'Empty dataset')
                        : t('dataAnalysis.noFilterResults', 'No results match your filters')}
                    </CardContent>
                  </Card>
                ) : (
                  filteredItems.map(item => (
                    <Card key={item.id} className="hover:shadow-sm transition-shadow">
                      <CardContent className="py-3 px-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm line-clamp-1">{item.title}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
                              {item.country && <Badge variant="outline" className="text-xs">{item.country}</Badge>}
                              {item.buyer_name && <span>{item.buyer_name}</span>}
                              {item.estimated_value && <span className="font-medium">€{item.estimated_value.toLocaleString()}</span>}
                              {item.publication_date && <span>{item.publication_date}</span>}
                            </div>
                            {item.cpv_codes && item.cpv_codes.length > 0 && (
                              <div className="flex gap-1 mt-1 flex-wrap">
                                {item.cpv_codes.slice(0, 3).map(c => <CpvBadge key={c} code={c} />)}
                                {item.cpv_codes.length > 3 && <Badge variant="secondary" className="text-xs">+{item.cpv_codes.length - 3}</Badge>}
                              </div>
                            )}
                          </div>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => removeItem(item.id)}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* AI Analysis Tab */}
            <TabsContent value="ai" className="space-y-4">
              {/* Analysis modules */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {analysisModules.map(mod => (
                  <Card
                    key={mod.key}
                    className={cn(
                      "cursor-pointer transition-all hover:shadow-md",
                      analysisType === mod.key && "ring-2 ring-primary"
                    )}
                    onClick={() => setAnalysisType(mod.key)}
                  >
                    <CardContent className="py-4 text-center">
                      <mod.icon className={cn("w-6 h-6 mx-auto mb-2", mod.color)} />
                      <p className="text-xs font-medium">{mod.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <Button onClick={runAnalysis} disabled={analyzing || datasetItems.length === 0} className="gap-2">
                  {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {analyzing
                    ? t('dataAnalysis.analyzing', 'Analyzing...')
                    : t('dataAnalysis.runAnalysis', 'Run Analysis')}
                </Button>
                {analysisResult && (
                  <Button variant="outline" onClick={exportPdf} className="gap-2">
                    <FileDown className="w-4 h-4" />
                    {t('dataAnalysis.exportPdf', 'Export PDF')}
                  </Button>
                )}
                <span className="text-sm text-muted-foreground">
                  {datasetItems.length} {t('dataAnalysis.tendersInDataset', 'tenders in dataset')}
                </span>
              </div>

              {/* Results */}
              {analysisResult && (
                <div className="space-y-4">
                  {/* Summary */}
                  {analysisResult.summary && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Brain className="w-4 h-4 text-primary" />
                          {t('dataAnalysis.summary', 'Summary')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">{analysisResult.summary}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Trends */}
                  {analysisResult.trends && analysisResult.trends.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.trendsTitle', 'Trends')}</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {analysisResult.trends.map((tr: any, i: number) => (
                            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                              <Badge variant={tr.impact === 'positive' ? 'default' : tr.impact === 'negative' ? 'destructive' : 'secondary'} className="text-xs mt-0.5">
                                {tr.impact}
                              </Badge>
                              <div>
                                <p className="font-medium text-sm">{tr.title}</p>
                                <p className="text-xs text-muted-foreground mt-1">{tr.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Top Opportunities */}
                  {analysisResult.topOpportunities && analysisResult.topOpportunities.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.topOpportunities', 'Top Opportunities')}</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {analysisResult.topOpportunities.map((op: any, i: number) => (
                            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-bold text-sm">
                                {op.score || i + 1}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-sm">{op.title}</p>
                                <p className="text-xs text-muted-foreground">{op.reason}</p>
                              </div>
                              {op.country && <Badge variant="outline" className="text-xs">{op.country}</Badge>}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Competitive Analysis */}
                  {analysisResult.competitiveAnalysis && (
                    <Card>
                      <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.competitiveTitle', 'Competitive Landscape')}</CardTitle></CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-3">{analysisResult.competitiveAnalysis.marketConcentration}</p>
                        {analysisResult.competitiveAnalysis.insights && (
                          <ul className="space-y-1">
                            {analysisResult.competitiveAnalysis.insights.map((ins: string, i: number) => (
                              <li key={i} className="text-sm flex items-start gap-2">
                                <span className="text-primary mt-1">•</span> {ins}
                              </li>
                            ))}
                          </ul>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Buyer concentration */}
                  {analysisResult.buyerConcentration && (
                    <Card>
                      <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.buyerConcentration', 'Buyer Concentration')}</CardTitle></CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">{analysisResult.buyerConcentration.analysis}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Dominant Buyers */}
                  {analysisResult.dominantBuyers && analysisResult.dominantBuyers.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.dominantBuyers', 'Dominant Buyers')}</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {analysisResult.dominantBuyers.map((b: any, i: number) => (
                            <div key={i} className="p-3 bg-muted/50 rounded-lg">
                              <p className="font-medium text-sm">{b.name}</p>
                              <p className="text-xs text-muted-foreground">{b.tenders} {t('dataAnalysis.tenders', 'tenders')} {b.totalValue && `• ${b.totalValue}`}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Predictions */}
                  {analysisResult.predictions && analysisResult.predictions.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.predictionsTitle', 'Predictions')}</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {analysisResult.predictions.map((p: any, i: number) => (
                            <div key={i} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                              <Badge variant={p.trend === 'growing' ? 'default' : p.trend === 'declining' ? 'destructive' : 'secondary'} className="text-xs">
                                {p.trend}
                              </Badge>
                              <div className="flex-1">
                                <p className="font-medium text-sm">{p.sector} {p.country && `(${p.country})`}</p>
                                <p className="text-xs text-muted-foreground">{p.reasoning}</p>
                              </div>
                              {p.confidence && <Badge variant="outline" className="text-xs">{p.confidence}</Badge>}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Pricing */}
                  {analysisResult.pricing && (
                    <Card>
                      <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.pricingTitle', 'Pricing Analysis')}</CardTitle></CardHeader>
                      <CardContent>
                        {analysisResult.pricing.trends && (
                          <ul className="space-y-1">
                            {analysisResult.pricing.trends.map((tr: string, i: number) => (
                              <li key={i} className="text-sm flex items-start gap-2">
                                <span className="text-primary mt-1">•</span> {tr}
                              </li>
                            ))}
                          </ul>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {analysisResult.priceRanges && analysisResult.priceRanges.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.priceRanges', 'Price Ranges by Sector')}</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {analysisResult.priceRanges.map((pr: any, i: number) => (
                            <div key={i} className="p-3 bg-muted/50 rounded-lg">
                              <p className="font-medium text-sm">{pr.sector}</p>
                              <p className="text-xs text-muted-foreground">
                                Min: €{pr.min?.toLocaleString()} • Max: €{pr.max?.toLocaleString()} • Avg: €{pr.average?.toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Deadline Analysis */}
                  {analysisResult.deadlineAnalysis && (
                    <Card>
                      <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.deadlineAnalysis', 'Deadline Analysis')}</CardTitle></CardHeader>
                      <CardContent className="space-y-3">
                        {analysisResult.deadlineAnalysis.urgent && (
                          <p className="text-sm"><span className="font-medium text-orange-500">{t('dataAnalysis.urgent', 'Urgent')}:</span> {analysisResult.deadlineAnalysis.urgent}</p>
                        )}
                        {analysisResult.deadlineAnalysis.expired && (
                          <p className="text-sm"><span className="font-medium text-destructive">{t('dataAnalysis.expired', 'Expired')}:</span> {analysisResult.deadlineAnalysis.expired}</p>
                        )}
                        {analysisResult.deadlineAnalysis.insights?.map((ins: string, i: number) => (
                          <p key={i} className="text-sm flex items-start gap-2">
                            <span className="text-primary mt-1">•</span> {ins}
                          </p>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                  {analysisResult.recommendations && analysisResult.recommendations.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-sm">{t('dataAnalysis.recommendations', 'Recommendations')}</CardTitle></CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {analysisResult.recommendations.map((rec: string, i: number) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">{i + 1}</span>
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
